/**
 * featureFlag.js
 *
 * @description Defines a feature flag class.
 */

export class FeatureFlag {
  /**
   * Creates an instance of FeatureFlag.
   * @param {any} identifier The flag's identifier.
   * @param {any} meta An array of meta values.
   * @param {boolean} [defaultValue=false] The default state of the flag.
   * @memberof FeatureFlag
   */
  constructor (identifier, meta, defaultValue = false) {
    this.identifier = identifier
    this.meta = meta
    this.defaultValue = defaultValue
  }

  /**
   * Determines if a flag is enabled.
   *
   * @returns `true` if the flag is enabled, else `false.`
   * @memberof FeatureFlag
   */
  get enabled () {
    const localValue = this.localValue

    if (localValue !== undefined) {
      return localValue
    }

    const environmentValue = this.environmentValue

    if (environmentValue !== undefined) {
      return environmentValue
      // console.debug(`Using environment variable for ${identifier}: ${localValue}`)
    }

    return this.defaultValue
  }

  get source () {
    const localValue = this.localValue

    if (localValue !== undefined) {
      return 'local'
    }

    const environmentValue = this.environmentValue

    if (environmentValue !== undefined) {
      return 'env'
      // console.debug(`Using environment variable for ${identifier}: ${localValue}`)
    }

    return 'default'
  }

  /**
   * Checks the local override value.
   *
   * @readonly
   * @memberof FeatureFlag
   */
  get localValue () {
    const identifier = this.identifier
    const { store } = require('@/store/store')
    if (store && store.getters) {
      const isSetLocally = store.getters['features/isSetLocally'](identifier)
      if (isSetLocally) {
        const localValue = store.getters['features/enabled'](identifier)
        // console.debug(`Using locally stored value for ${identifier}: ${localValue}`)
        return localValue
      }
    }
    return undefined
  }

  /**
   * Checks the value of the flag based on the environment variable.
   *
   * @readonly
   * @memberof FeatureFlag
   */
  get environmentValue () {
    const identifier = this.identifier
    const environment = process.env
    const setByEnvironment = identifier in environment

    if (setByEnvironment) {
      const localValue = environment[identifier]
      // console.debug(`Using environment variable for ${identifier}: ${localValue}`)
      return localValue
    }
    return undefined
  }

  /**
   * Toggles a feature flag locally.
   *
   * @param {Boolean} enabled The new state of the feature.
   * @memberof FeatureFlag
   */
  setEnabled (enabled) {
    const store = require('@/store/store')
    if (store) {
      store.dispatch('features/setEnabled', this.identifier, enabled)
    }
  }

  /**
   * Clear the local override.
   *
   * @memberof FeatureFlag
   */
  clearOverride () {
    const store = require('@/store/store')
    if (store) {
      store.dispatch('features/clear', this.identifier)
    }
  }
}
